import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  uploadQuestions,
} from '../lib/api';
import { SUBJECTS } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  FileQuestion,
  Plus,
  Edit,
  Trash2,
  Upload,
  Search,
  Loader2,
  CheckCircle,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 200;

const AdminQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedSubject, setSelectedSubject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    subject: '',
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_option: '',
  });

  const subjectList = Object.values(SUBJECTS);

  // Evita race e respostas antigas sobrescrevendo novas
  const reqIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  useEffect(() => {
    setPage(1);
  }, [selectedSubject, debouncedSearch]);

  const loadQuestions = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);

    try {
      const subject = selectedSubject === 'all' ? null : selectedSubject;
      const response = await getQuestions(subject);

      if (reqId !== reqIdRef.current) return;

      setQuestions(Array.isArray(response.data) ? response.data : []);
    } catch {
      if (reqId === reqIdRef.current) toast.error('Erro ao carregar questões');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const getQuestionId = (q) => q?.id || q?._id;

  const handleOpenDialog = (question = null) => {
    if (question) {
      setFormData({
        subject: question.subject ?? '',
        question_text: question.question_text ?? '',
        option_a: question.option_a ?? '',
        option_b: question.option_b ?? '',
        option_c: question.option_c ?? '',
        option_d: question.option_d ?? '',
        correct_option: question.correct_option ?? '',
      });
      setSelectedQuestion(question);
    } else {
      setFormData({
        subject: '',
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_option: '',
      });
      setSelectedQuestion(null);
    }
    setDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!formData.subject || !formData.question_text || !formData.correct_option) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (selectedQuestion) {
        const id = getQuestionId(selectedQuestion);
        await updateQuestion(id, formData);
        toast.success('Questão atualizada com sucesso');
      } else {
        await createQuestion(formData);
        toast.success('Questão criada com sucesso');
      }

      setDialogOpen(false);
      await loadQuestions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar questão');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;

    try {
      const id = getQuestionId(selectedQuestion);
      await deleteQuestion(id);
      toast.success('Questão excluída com sucesso');

      setDeleteDialogOpen(false);
      setSelectedQuestion(null);
      await loadQuestions();
    } catch {
      toast.error('Erro ao excluir questão');
    }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadQuestions(file);

      const added = response?.data?.added ?? 0;
      const skippedDuplicates = response?.data?.skipped_duplicates ?? 0;
      toast.success(`${added} questões adicionadas`);

      if (skippedDuplicates > 0) {
        toast.info(`${skippedDuplicates} questões duplicadas ignoradas`);
      }

      const errorsCount = response?.data?.errors?.length ?? 0;
      if (errorsCount > 0) {
        toast.error(`${errorsCount} erros encontrados`);
      }

      await loadQuestions();
    } catch {
      toast.error('Erro ao fazer upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const filteredQuestions = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase();
    if (!s) return questions;

    return questions.filter((q) =>
      (q?.question_text ?? '').toLowerCase().includes(s)
    );
  }, [questions, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / PAGE_SIZE));

  const pagedQuestions = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredQuestions.slice(start, start + PAGE_SIZE);
  }, [filteredQuestions, page, totalPages]);

  const handleExportCSV = () => {
    try {
      const headers = [
        'subject',
        'question_text',
        'option_a',
        'option_b',
        'option_c',
        'option_d',
        'correct_option',
      ];

      const escapeCSV = (value) => {
        const s = String(value ?? '');
        const needsQuotes = /[",\n\r]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      // Exporta TUDO que está filtrado, não só a página atual
      const rows = filteredQuestions.map((q) => [
        q.subject ?? '',
        q.question_text ?? '',
        q.option_a ?? '',
        q.option_b ?? '',
        q.option_c ?? '',
        q.option_d ?? '',
        q.correct_option ?? '',
      ]);

      const csv = [
        headers.join(','),
        ...rows.map((r) => r.map(escapeCSV).join(',')),
      ].join('\n');

      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'questions_export.csv';

      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);

      toast.success('CSV exportado com sucesso');
    } catch {
      toast.error('Erro ao exportar CSV');
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fadeIn">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center">
              <FileQuestion className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-zinc-900">Questões</h1>
              <p className="text-zinc-500">
                {filteredQuestions.length} questões encontradas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={filteredQuestions.length === 0}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>

            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv"
                onChange={handleUploadCSV}
                className="hidden"
                data-testid="upload-csv-input"
              />
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload CSV
                </span>
              </Button>
            </label>

            <Button
              onClick={() => handleOpenDialog()}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Questão
            </Button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  placeholder="Buscar questão..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-questions"
                />
              </div>
            </div>

            <div className="w-full md:w-64">
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger data-testid="filter-subject">
                  <SelectValue placeholder="Filtrar por matéria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as matérias</SelectItem>
                  {subjectList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-20 bg-white border border-zinc-200 rounded-xl">
            <FileQuestion className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">Nenhuma questão encontrada</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {pagedQuestions.map((question) => {
                const qid = getQuestionId(question);

                return (
                  <div
                    key={qid}
                    className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-zinc-300 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <span className="inline-block px-2 py-1 bg-zinc-100 text-zinc-600 text-xs rounded mb-2">
                          {SUBJECTS[question.subject]?.name || question.subject}
                        </span>

                        <p className="text-zinc-900 mb-4">{question.question_text}</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {['a', 'b', 'c', 'd'].map((opt) => {
                            const isCorrect = question.correct_option === opt;
                            const optValue = question[`option_${opt}`] ?? '';

                            return (
                              <div
                                key={opt}
                                className={`flex items-center gap-2 p-2 rounded ${
                                  isCorrect
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-zinc-50 text-zinc-600'
                                }`}
                              >
                                <span
                                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                                    isCorrect ? 'bg-emerald-200' : 'bg-zinc-200'
                                  }`}
                                >
                                  {opt.toUpperCase()}
                                </span>

                                <span className="truncate">{optValue}</span>

                                {isCorrect && (
                                  <CheckCircle className="w-4 h-4 ml-auto flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(question)}
                          data-testid={`edit-question-${qid}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedQuestion(question);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-question-${qid}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-6">
              <div className="text-sm text-zinc-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1} a{' '}
                {Math.min(page * PAGE_SIZE, filteredQuestions.length)} de{' '}
                {filteredQuestions.length}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>

                <div className="text-sm text-zinc-600 px-2">
                  Página {page} de {totalPages}
                </div>

                <Button
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Próxima
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedQuestion ? 'Editar Questão' : 'Nova Questão'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Matéria *</Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                >
                  <SelectTrigger data-testid="form-subject">
                    <SelectValue placeholder="Selecione a matéria" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectList.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Enunciado da Questão *</Label>
                <Textarea
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  placeholder="Digite o enunciado..."
                  rows={3}
                  data-testid="form-question-text"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['a', 'b', 'c', 'd'].map((opt) => (
                  <div key={opt}>
                    <Label>Opção {opt.toUpperCase()}</Label>
                    <Input
                      value={formData[`option_${opt}`]}
                      onChange={(e) =>
                        setFormData({ ...formData, [`option_${opt}`]: e.target.value })
                      }
                      placeholder={`Opção ${opt.toUpperCase()}`}
                      data-testid={`form-option-${opt}`}
                    />
                  </div>
                ))}
              </div>

              <div>
                <Label>Resposta Correta *</Label>
                <Select
                  value={formData.correct_option}
                  onValueChange={(value) => setFormData({ ...formData, correct_option: value })}
                >
                  <SelectTrigger data-testid="form-correct-option">
                    <SelectValue placeholder="Selecione a resposta correta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a">A</SelectItem>
                    <SelectItem value="b">B</SelectItem>
                    <SelectItem value="c">C</SelectItem>
                    <SelectItem value="d">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>

              <Button
                onClick={handleSaveQuestion}
                disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="save-question-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {selectedQuestion ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Questão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir esta questão? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteQuestion}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-question"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminQuestionsPage;
